<template>
    <div>
        <div class="flex flex-col md:flex-row flex-wrap items-stretch justify-center gap-6 md:gap-8" v-if="plans">
            <!-- Free Try -->
            <div v-if="visiblePlans.freeTry" class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card>
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{ t('pricing.free-try')
                        }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            t('pricing.try-before-you-buy')
                        }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col flex-1">
                            <div v-if="getDescription(freeTryProduct!) !== 'none'"
                                class="text-sm text-color-secondary mb-2">
                                {{ getDescription(freeTryProduct!) }}
                            </div>
                            <div v-if="getFeatures(freeTryProduct!) !== 'none'">
                                <Markdown :content="getFeatures(freeTryProduct!)" />
                            </div>
                            <div class="mt-auto text-center">
                                <div>
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro(0) }}{{ currencySymbol
                                    }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base">
                                    {{ t('pricing.free-trial') }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" @click="emit('choose', { plan: 'free_try' })" />
                    </template>
                </Card>
            </div>

            <!-- Early access (aggregate sub prices) -->
            <div v-if="visiblePlans.earlyAccess && earlyAccessQmsProduct && earlyAccessQmsPrice && earlyAccessPlatformProduct && earlyAccessPlatformPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card class="ring-1 ring-primary-200/40">
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{
                            t('pricing.early-access-title') }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            t('pricing.early-access-subtitle')
                        }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col gap-1.5 flex-1">
                            <!-- Platform Access Description and Features -->
                            <div v-if="getDescription(earlyAccessPlatformProduct) !== 'none'"
                                class="mb-2 text-sm text-color-secondary">
                                {{ getDescription(earlyAccessPlatformProduct) }}
                            </div>
                            <div v-if="getFeatures(earlyAccessPlatformProduct) !== 'none'" class="text-left">
                                <Markdown :content="getFeatures(earlyAccessPlatformProduct)" />
                            </div>

                            <!-- QMS Description and Features -->
                            <div v-if="getDescription(earlyAccessQmsProduct) !== 'none'"
                                class="mb-2 text-sm text-color-secondary">
                                {{ getDescription(earlyAccessQmsProduct) }}
                            </div>
                            <div v-if="getFeatures(earlyAccessQmsProduct) !== 'none'">
                                <Markdown :content="getFeatures(earlyAccessQmsProduct)" />
                            </div>

                            <div class="mt-auto">
                                <!-- Platform Access Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="getTitle(earlyAccessPlatformProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(earlyAccessPlatformProduct) }}</span>
                                        <span class="text-sm font-semibold">{{
                                            formatEuro(earlyAccessPlatformPrice.unit_amount) }}{{ currencySymbol
                                            }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-user-month') }}</div>
                                </div>

                                <!-- QMS Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="getTitle(earlyAccessQmsProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(earlyAccessQmsProduct) }}</span>
                                        <span class="text-sm font-semibold">{{
                                            formatEuro(earlyAccessQmsPrice.unit_amount)
                                        }}{{ currencySymbol }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-user-project-month') }}
                                    </div>
                                </div>

                                <!-- Coupon Code -->
                                <div class="mb-2 p-2 bg-primary-50 rounded-lg border border-primary-200">
                                    <div class="text-center">
                                        <div class="text-sm font-semibold text-primary-700 mb-1">{{
                                            t('pricing.first-month-free') }}</div>
                                        <div class="text-xs text-primary-600">{{ t('pricing.coupon-code') }} <span
                                                class="font-mono font-bold">{{ COUPON_CODE }}</span></div>
                                    </div>
                                </div>
                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro((earlyAccessQmsPrice.unit_amount
                                        ?? 0) +
                                        (earlyAccessPlatformPrice.unit_amount ?? 0))
                                    }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">
                                    {{ t('pricing.starting-from') }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button :disabled="false" size="large" :label="t('common.choose')" icon="pi pi-arrow-right"
                            icon-pos="right" class="w-full py-3 text-base"
                            @click="emit('choose', { plan: 'early_access' })" />
                    </template>
                </Card>
            </div>

            <!-- Evaluator (aggregate sub prices, disabled) -->
            <div v-if="visiblePlans.evaluator && evaluatorQmsProduct && evaluatorQmsPrice && evaluatorPlatformProduct && evaluatorPlatformPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card>
                    <template #title><span class="text-xl md:text-2xl font-semibold text-center block">{{
                        t('pricing.standard')
                            }}</span></template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            t('pricing.standard-subtitle')
                        }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col gap-1.5 flex-1">
                            <!-- Platform Access Description and Features -->
                            <div v-if="getDescription(evaluatorPlatformProduct) !== 'none'"
                                class="mb-2 text-sm text-color-secondary">
                                {{ getDescription(evaluatorPlatformProduct) }}
                            </div>
                            <div v-if="getFeatures(evaluatorPlatformProduct) !== 'none'">
                                <Markdown :content="getFeatures(evaluatorPlatformProduct)" />
                            </div>

                            <!-- QMS Description and Features -->
                            <div v-if="getDescription(evaluatorQmsProduct) !== 'none'"
                                class="mb-2 text-sm text-color-secondary">
                                {{ getDescription(evaluatorQmsProduct) }}
                            </div>
                            <div v-if="getFeatures(evaluatorQmsProduct) !== 'none'">
                                <Markdown :content="getFeatures(evaluatorQmsProduct)" />
                            </div>

                            <div class="mt-auto">
                                <!-- Platform Access Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="getTitle(evaluatorPlatformProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(evaluatorPlatformProduct) }}</span>
                                        <span class="text-sm font-semibold">{{
                                            formatEuro(evaluatorPlatformPrice.unit_amount)
                                            }}{{ currencySymbol }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-user-month') }}</div>
                                </div>

                                <!-- QMS Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="getTitle(evaluatorQmsProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(evaluatorQmsProduct) }}</span>
                                        <span class="text-sm font-semibold">{{ formatEuro(evaluatorQmsPrice.unit_amount)
                                            }}{{ currencySymbol }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-user-project-month') }}
                                    </div>
                                </div>

                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro((evaluatorQmsPrice.unit_amount ??
                                        0)
                                        +
                                        (evaluatorPlatformPrice.unit_amount ?? 0))
                                    }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">
                                    {{ t('pricing.starting-from') }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" @click="emit('choose', { plan: 'evaluator' })" />
                    </template>
                </Card>
            </div>

            <!-- Student plan (aggregate sub prices) -->
            <div v-if="visiblePlans.student && studentQmsProduct && studentQmsPrice && studentPlatformProduct && studentPlatformPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card>
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{
                            t('pricing.student-title') }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            t('pricing.student-subtitle')
                        }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col gap-1.5 flex-1">
                            <!-- Platform Access Description and Features -->
                            <div v-if="getDescription(studentPlatformProduct) !== 'none'"
                                class="mb-2 text-sm text-color-secondary">
                                {{ getDescription(studentPlatformProduct) }}
                            </div>
                            <div v-if="getFeatures(studentPlatformProduct) !== 'none'" class="text-left">
                                <Markdown :content="getFeatures(studentPlatformProduct)" />
                            </div>

                            <!-- QMS Description and Features -->
                            <div v-if="getDescription(studentQmsProduct) !== 'none'"
                                class="mb-2 text-sm text-color-secondary">
                                {{ getDescription(studentQmsProduct) }}
                            </div>
                            <div v-if="getFeatures(studentQmsProduct) !== 'none'">
                                <Markdown :content="getFeatures(studentQmsProduct)" />
                            </div>

                            <div class="mt-auto">
                                <!-- Platform Access Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="getTitle(studentPlatformProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(studentPlatformProduct) }}</span>
                                        <span class="text-sm font-semibold">{{
                                            formatEuro(studentPlatformPrice.unit_amount) }}{{ currencySymbol
                                            }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-user-month') }}</div>
                                </div>

                                <!-- QMS Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="getTitle(studentQmsProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(studentQmsProduct) }}</span>
                                        <span class="text-sm font-semibold">{{
                                            formatEuro(studentQmsPrice.unit_amount)
                                        }}{{ currencySymbol }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-user-project-month') }}
                                    </div>
                                </div>

                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro((studentQmsPrice.unit_amount ??
                                        0) +
                                        (studentPlatformPrice.unit_amount ?? 0))
                                    }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">
                                    {{ t('pricing.starting-from') }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" @click="emit('choose', { plan: 'student' })" />
                    </template>
                </Card>
            </div>

            <!-- Single assessment (uses FREE plan under the hood) -->
            <div v-if="visiblePlans.free && singleAssessmentProduct && singleAssessmentPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card>
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{
                            t('pricing.single-assessment') }}</span>
                    </template>
                    <template #subtitle>
                        <span v-if="getDescription(singleAssessmentProduct) !== 'none'"
                            class="text-base md:text-lg text-color-secondary text-center block">{{
                                getDescription(singleAssessmentProduct)
                            }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col flex-1">
                            <div v-if="getFeatures(singleAssessmentProduct) !== 'none'">
                                <Markdown :content="getFeatures(singleAssessmentProduct)" />
                            </div>
                            <div v-if="freePlatformProduct && getFeatures(freePlatformProduct) !== 'none'">
                                <Markdown :content="getFeatures(freePlatformProduct)" />
                            </div>
                            <div class="mt-auto">
                                <!-- Platform Access Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span v-if="freePlatformProduct && getTitle(freePlatformProduct) !== 'none'"
                                            class="text-sm font-medium text-color-secondary">{{
                                                getTitle(freePlatformProduct) }}</span>
                                        <span v-else class="text-sm font-medium text-color-secondary">{{
                                            t('pricing.platform-access') }}</span>
                                        <span class="text-sm font-semibold">{{ formatEuro(0) }}{{ currencySymbol
                                        }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.limited-to-one-user') }}
                                    </div>
                                </div>

                                <!-- Assessment Pricing -->
                                <div class="mb-2 p-2 bg-surface-50 rounded-lg">
                                    <div class="flex justify-between items-center mb-1">
                                        <span class="text-sm font-medium text-color-secondary">{{
                                            t('pricing.assessment') }}</span>
                                        <span class="text-sm font-semibold">{{
                                            formatEuro(singleAssessmentPrice.unit_amount)
                                        }}{{ currencySymbol }}</span>
                                    </div>
                                    <div class="text-xs text-color-secondary">{{ t('pricing.per-assessment') }}</div>
                                </div>

                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{
                                        formatEuro(singleAssessmentPrice.unit_amount)
                                    }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">
                                    {{ t('pricing.one-time-payment') }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" @click="emit('choose', { plan: 'free' })" />
                    </template>
                </Card>
            </div>

            <!-- Veille (credit plan) -->
            <div v-if="visiblePlans.veille && veilleProduct && veillePrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card>
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{ t('pricing.veille') }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            getDescription(veilleProduct) || t('pricing.veille-subtitle') }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col flex-1">
                            <div class="text-left">
                                <Markdown :content="getFeatures(veilleProduct) || t('pricing.veille-features')" />
                            </div>
                            <div class="mt-auto">
                                <div class="mb-2 p-2 bg-primary-50 rounded-lg border border-primary-200">
                                    <div class="text-center">
                                        <div class="text-sm font-semibold text-primary-700">
                                            {{ formatCredits(veillePrice.metadata?.credits) }} {{ t('pricing.credits-per-month') }}
                                        </div>
                                    </div>
                                </div>
                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro(veillePrice.unit_amount) }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">{{ t('pricing.per-month') }}</div>
                                <div v-if="!veillePrice.unit_amount" class="text-sm text-color-secondary text-center">{{ t('pricing.no-credit-card') }}</div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" @click="emit('choose', { plan: 'veille', priceLookupKey: PriceLookupKeys.VEILLE })" />
                    </template>
                </Card>
            </div>

            <!-- QMS (credit plan) -->
            <div v-if="visiblePlans.qms && qmsProduct && qmsPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card class="ring-1 ring-primary-200/40">
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{ t('pricing.qms') }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            getDescription(qmsProduct) || t('pricing.qms-subtitle') }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col flex-1">
                            <div class="text-left">
                                <Markdown :content="getFeatures(qmsProduct) || t('pricing.qms-features')" />
                            </div>
                            <div class="mt-auto">
                                <div v-if="qmsTierOptions.length > 1" class="mb-3">
                                    <Select v-model="selectedQmsTier"
                                        :options="qmsTierOptions" optionLabel="label" optionValue="value"
                                        :placeholder="t('billing.tiers.select')" class="w-full" />
                                </div>
                                <div class="mb-2 p-2 bg-primary-50 rounded-lg border border-primary-200">
                                    <div class="text-center">
                                        <div class="text-sm font-semibold text-primary-700">
                                            {{ formatCredits(qmsPrice.metadata?.credits) }} {{ t('pricing.credits-per-month') }}
                                        </div>
                                    </div>
                                </div>
                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro(qmsPrice.unit_amount) }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">{{ t('pricing.per-month') }}</div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" :disabled="isCurrentTier(selectedQmsTier)"
                            @click="emit('choose', { plan: 'qms', priceLookupKey: selectedQmsTier })" />
                    </template>
                </Card>
            </div>

            <!-- TD (credit plan) -->
            <div v-if="visiblePlans.td && tdProduct && tdPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card class="ring-1 ring-primary-200/40">
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{ t('pricing.td') }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            getDescription(tdProduct) || t('pricing.td-subtitle') }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col flex-1">
                            <div class="text-left">
                                <Markdown :content="getFeatures(tdProduct) || t('pricing.td-features')" />
                            </div>
                            <div class="mt-auto">
                                <div v-if="tdTierOptions.length > 1" class="mb-3">
                                    <Select v-model="selectedTdTier"
                                        :options="tdTierOptions" optionLabel="label" optionValue="value"
                                        :placeholder="t('billing.tiers.select')" class="w-full" />
                                </div>
                                <div class="mb-2 p-2 bg-primary-50 rounded-lg border border-primary-200">
                                    <div class="text-center">
                                        <div class="text-sm font-semibold text-primary-700">
                                            {{ formatCredits(tdPrice.metadata?.credits) }} {{ t('pricing.credits-per-month') }}
                                        </div>
                                    </div>
                                </div>
                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro(tdPrice.unit_amount) }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">{{ t('pricing.per-month') }}</div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" :disabled="isCurrentTier(selectedTdTier)"
                            @click="emit('choose', { plan: 'td', priceLookupKey: selectedTdTier })" />
                    </template>
                </Card>
            </div>

            <!-- QMS + TD (credit plan) -->
            <div v-if="visiblePlans.qmsTd && qmsTdProduct && qmsTdPrice"
                class="pricing-card-wrapper min-w-[280px] flex-1">
                <Card>
                    <template #title>
                        <span class="text-xl md:text-2xl font-semibold text-center block">{{ t('pricing.qms-td') }}</span>
                    </template>
                    <template #subtitle>
                        <span class="text-base md:text-lg text-color-secondary text-center block">{{
                            getDescription(qmsTdProduct) || t('pricing.qms-td-subtitle') }}</span>
                    </template>
                    <template #content>
                        <div class="flex flex-col flex-1">
                            <div class="text-left">
                                <Markdown :content="getFeatures(qmsTdProduct) || t('pricing.qms-td-features')" />
                            </div>
                            <div class="mt-auto">
                                <div v-if="qmsTdTierOptions.length > 1" class="mb-3">
                                    <Select v-model="selectedQmsTdTier"
                                        :options="qmsTdTierOptions" optionLabel="label" optionValue="value"
                                        :placeholder="t('billing.tiers.select')" class="w-full" />
                                </div>
                                <div class="mb-2 p-2 bg-primary-50 rounded-lg border border-primary-200">
                                    <div class="text-center">
                                        <div class="text-sm font-semibold text-primary-700">
                                            {{ formatCredits(qmsTdPrice.metadata?.credits) }} {{ t('pricing.credits-per-month') }}
                                        </div>
                                    </div>
                                </div>
                                <div class="text-center">
                                    <strong class="text-2xl md:text-3xl">{{ formatEuro(qmsTdPrice.unit_amount) }}{{ currencySymbol }}</strong>
                                    <span class="ml-2">{{ t('pricing.ht') }}</span>
                                </div>
                                <div class="text-base text-center">{{ t('pricing.per-month') }}</div>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <Button size="large" :label="t('common.choose')" icon="pi pi-arrow-right" icon-pos="right"
                            class="w-full py-3 text-base" :disabled="isCurrentTier(selectedQmsTdTier)"
                            @click="emit('choose', { plan: 'qms_td', priceLookupKey: selectedQmsTdTier })" />
                    </template>
                </Card>
            </div>
        </div>

        <!-- All plans include -->
        <p v-if="showCreditPlans" class="text-sm text-color-secondary text-center mt-4">
            {{ t('pricing.all-plans-include') }}
        </p>

        <!-- Contact Sales Card -->
        <Card v-if="plans" class="max-w-lg mx-auto mt-4">
            <template #content>
                <div class="flex flex-col gap-1.5 py-1.5">
                    <h3 class="text-base font-semibold text-center">{{ t('billing.planSwitch.contactSales.title') }}
                    </h3>
                    <p class="text-color-secondary text-center">{{ t('billing.planSwitch.contactSales.description')
                        }}
                    </p>
                    <a :href="contactSalesUrl" :target="contactSalesIsHttp ? '_blank' : undefined"
                        :rel="contactSalesIsHttp ? 'noopener noreferrer' : undefined"
                        class="text-primary hover:underline inline-flex items-center gap-1 mt-1 justify-center">
                        {{ t('billing.planSwitch.contactSales.link') }}
                    </a>
                    <Button v-if="legacyPlansEnabled" :label="t('pricing.have-access-code')" outlined class="mt-2"
                        @click="showAccessCodeDialog = true" />
                </div>
            </template>
        </Card>

        <!-- Access Code Dialog -->
        <Dialog v-model:visible="showAccessCodeDialog" modal :header="t('pricing.enter-access-code')"
            :style="{ width: '400px' }">
            <template #default>
                <div class="flex flex-col gap-3">
                    <div>
                        <label for="access-code-input" class="block text-sm font-medium mb-2">{{
                            t('pricing.access-code') }}</label>
                        <InputText id="access-code-input" v-model="accessCode"
                            :placeholder="t('pricing.enter-access-code')" class="w-full"
                            @keyup.enter="validateAccessCode" />
                    </div>
                    <InlineMessage v-if="accessCodeError" severity="error" :closable="false">
                        {{ t('pricing.invalid-code') }}
                    </InlineMessage>
                </div>
            </template>
            <template #footer>
                <Button :label="t('shared.cancel')" text @click="closeAccessCodeDialog" />
                <Button :label="t('pricing.unlock-plan')" @click="validateAccessCode" :disabled="!accessCode.trim()" />
            </template>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import InlineMessage from 'primevue/inlinemessage'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Select from 'primevue/select'
import { useI18n } from 'vue-i18n'
import Markdown from '@/components/shared/Markdown.vue'
import type { StripeProduct } from '@/models/Billing'
import { TenantPlan } from '@/models/Tenant'
import { PriceLookupKeys, type PricingPlan, type PricingPlans } from '@/models/Billing'
import { NO_PLANS, type VisiblePlans } from '@/composables/planVisibility'
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import { useAppStore } from '@/stores/app'
import { DEFAULT_CONTACT_SALES_URL } from '@/constants/billing'

type PlanChoice = 'free' | 'free_try' | 'early_access' | 'evaluator' | 'student' | 'veille' | 'qms' | 'td' | 'qms_td'

const emit = defineEmits<{
    (e: 'choose', payload: { plan: PlanChoice; priceLookupKey?: string }): void
}>()

// Emission handled inline in template

const { t, locale } = useI18n()

const props = defineProps<{
    plans: PricingPlans | null
    /** Partial is tolerated, but an absent key means HIDDEN — see NO_PLANS. */
    visiblePlans?: Partial<VisiblePlans>
    currentPriceLookupKey?: string | null
}>()

const appStore = useAppStore()
// Both access codes unlock a legacy plan (student, early access), so the whole
// entry point goes away with the legacy lineup — the concept no longer applies,
// which is the one case where hiding beats disabling.
const legacyPlansEnabled = computed(() => useFeatureFlagsStore().isLegacyPlansEnabled)

// Contact-sales URL is configurable via system/public_config; fall back to the
// shared default (public contact page) if unset.
const contactSalesUrl = computed(() => appStore.publicConfig?.contact_sales_url || DEFAULT_CONTACT_SALES_URL)
// Open http(s) links in a new tab; mailto:/tel: must stay same-tab (else some
// browsers leave a blank tab open before handing off to the mail client).
const contactSalesIsHttp = computed(() => /^https?:\/\//i.test(contactSalesUrl.value))

// Access code state
const accessCode = ref('')
const accessCodeError = ref(false)
const unlockedPlanType = ref<'early_access' | 'student' | null>(null)
const showAccessCodeDialog = ref(false)

// Deny by default. This component is presentation only: the offerable set is
// decided in `composables/planVisibility.ts` and passed in. Defaulting a card to
// `true` here is what put the retired legacy cards back on prod's resubscribe
// screen, so an unnamed key stays hidden.
const defaultVisiblePlans = NO_PLANS

function validateAccessCode() {
    accessCodeError.value = false
    unlockedPlanType.value = null

    if (!accessCode.value.trim()) {
        return
    }

    // A code may not resurrect a card the platform no longer sells.
    if (!legacyPlansEnabled.value) {
        accessCodeError.value = true
        return
    }

    const code = accessCode.value.trim().toUpperCase()
    const config = appStore.config

    // Check student codes
    if (config?.student_codes && config.student_codes.includes(code)) {
        unlockedPlanType.value = 'student'
        closeAccessCodeDialog()
        return
    }

    // Check early access codes
    if (config?.early_access_codes && config.early_access_codes.includes(code)) {
        unlockedPlanType.value = 'early_access'
        closeAccessCodeDialog()
        return
    }

    // Invalid code
    accessCodeError.value = true
}

function closeAccessCodeDialog() {
    showAccessCodeDialog.value = false
    accessCodeError.value = false
}

const visiblePlans = computed(() => {
    const base = {
        ...defaultVisiblePlans,
        ...props.visiblePlans
    }

    // If a code is unlocked, show only that plan
    if (unlockedPlanType.value === 'student') {
        return {
            ...base,
            freeTry: false,
            free: false,
            earlyAccess: false,
            evaluator: false,
            student: true
        }
    }

    if (unlockedPlanType.value === 'early_access') {
        return {
            ...base,
            freeTry: false,
            free: false,
            earlyAccess: true,
            evaluator: false,
            student: false
        }
    }

    // Default: show free and evaluator
    return base
})

const showCreditPlans = computed(() => visiblePlans.value.veille || visiblePlans.value.qms || visiblePlans.value.td || visiblePlans.value.qmsTd)

const freeTryPlan = computed(() => props.plans?.getByPlan(TenantPlan.FREE_TRY) || null)
const freePlan = computed(() => props.plans?.getByPlan(TenantPlan.FREE) || null)
const earlyAccessPlan = computed(() => props.plans?.getByPlan(TenantPlan.EARLY_ACCESS) || null)
const evaluatorPlan = computed(() => props.plans?.getByPlan(TenantPlan.EVALUATOR) || null)
const studentPlan = computed(() => props.plans?.getByPlan(TenantPlan.STUDENT) || null)

const freeTry = computed(() => freeTryPlan.value?.getByPriceLookupKey(PriceLookupKeys.FREE_TRY) || null)
const freeTryProduct = computed(() => freeTry.value?.product || null)
const freeTryPrice = computed(() => freeTry.value?.price || null)

// Single assessment is the FREE plan + one assessment; display but emit FREE plan
const singleAssessment = computed(() =>
    freePlan.value?.getByPriceLookupKey(PriceLookupKeys.SINGLE_ASSESSMENT) ||
    props.plans?.getPricingByLookupKey(PriceLookupKeys.SINGLE_ASSESSMENT) ||
    null
)
const singleAssessmentProduct = computed(() => singleAssessment.value?.product || null)
const singleAssessmentPrice = computed(() => singleAssessment.value?.price || null)

const freePlatform = computed(() => freePlan.value?.getByPriceLookupKey(PriceLookupKeys.PLATFORM_ACCESS_FREE) || null)
const freePlatformProduct = computed(() => freePlatform.value?.product || null)

const earlyAccessPlatform = computed(() => earlyAccessPlan.value?.getByPriceLookupKey(PriceLookupKeys.EARLY_ACCESS_PLATFORM_ACCESS) || null)
const earlyAccessQms = computed(() => earlyAccessPlan.value?.getByPriceLookupKey(PriceLookupKeys.EARLY_ACCESS_QMS) || null)
const earlyAccessPlatformProduct = computed(() => earlyAccessPlatform.value?.product || null)
const earlyAccessPlatformPrice = computed(() => earlyAccessPlatform.value?.price || null)
const earlyAccessQmsProduct = computed(() => earlyAccessQms.value?.product || null)
const earlyAccessQmsPrice = computed(() => earlyAccessQms.value?.price || null)

const evaluatorPlatform = computed(() => evaluatorPlan.value?.getByPriceLookupKey(PriceLookupKeys.PLATFORM_ACCESS_EVALUATOR) || null)
const evaluatorQms = computed(() => evaluatorPlan.value?.getByPriceLookupKey(PriceLookupKeys.QMS_EVALUATOR) || null)
const evaluatorTd = computed(() => evaluatorPlan.value?.getByPriceLookupKey(PriceLookupKeys.TD_EVALUATOR) || null)
const evaluatorPlatformProduct = computed(() => evaluatorPlatform.value?.product || null)
const evaluatorPlatformPrice = computed(() => evaluatorPlatform.value?.price || null)
const evaluatorQmsProduct = computed(() => evaluatorQms.value?.product || null)
const evaluatorQmsPrice = computed(() => evaluatorQms.value?.price || null)
const evaluatorTdProduct = computed(() => evaluatorTd.value?.product || null)
const evaluatorTdPrice = computed(() => evaluatorTd.value?.price || null)

const studentPlatform = computed(() => studentPlan.value?.getByPriceLookupKey(PriceLookupKeys.PLATFORM_ACCESS_STUDENT) || null)
const studentQms = computed(() => studentPlan.value?.getByPriceLookupKey(PriceLookupKeys.QMS_STUDENT) || null)
const studentTd = computed(() => studentPlan.value?.getByPriceLookupKey(PriceLookupKeys.TD_STUDENT) || null)
const studentPlatformProduct = computed(() => studentPlatform.value?.product || null)
const studentPlatformPrice = computed(() => studentPlatform.value?.price || null)
const studentQmsProduct = computed(() => studentQms.value?.product || null)
const studentQmsPrice = computed(() => studentQms.value?.price || null)
const studentTdProduct = computed(() => studentTd.value?.product || null)
const studentTdPrice = computed(() => studentTd.value?.price || null)

// Credit-based plans
const veillePlan = computed(() => props.plans?.getByPlan(TenantPlan.VEILLE) || null)
const veillePricing = computed(() => veillePlan.value?.getByPriceLookupKey(PriceLookupKeys.VEILLE) || null)
const veilleProduct = computed(() => veillePricing.value?.product || null)
const veillePrice = computed(() => veillePricing.value?.price || null)

// Tier selection state for S/M/L credit plans
function defaultTier(keys: PriceLookupKeys[], fallback: PriceLookupKeys): PriceLookupKeys {
    const current = props.currentPriceLookupKey as PriceLookupKeys
    if (current && keys.includes(current)) return current
    return fallback
}

const selectedQmsTier = ref<PriceLookupKeys>(defaultTier([PriceLookupKeys.QMS_S, PriceLookupKeys.QMS_M, PriceLookupKeys.QMS_L], PriceLookupKeys.QMS_S))
const selectedTdTier = ref<PriceLookupKeys>(defaultTier([PriceLookupKeys.TD_S, PriceLookupKeys.TD_M, PriceLookupKeys.TD_L], PriceLookupKeys.TD_S))
const selectedQmsTdTier = ref<PriceLookupKeys>(defaultTier([PriceLookupKeys.QMS_TD_S, PriceLookupKeys.QMS_TD_M, PriceLookupKeys.QMS_TD_L], PriceLookupKeys.QMS_TD_S))

const qmsPlan = computed(() => props.plans?.getByPlan(TenantPlan.QMS) || null)
const qmsPricing = computed(() => qmsPlan.value?.getByPriceLookupKey(selectedQmsTier.value) || null)
const qmsProduct = computed(() => qmsPricing.value?.product || null)
const qmsPrice = computed(() => qmsPricing.value?.price || null)

const tdPlan = computed(() => props.plans?.getByPlan(TenantPlan.TD) || null)
const tdPricing = computed(() => tdPlan.value?.getByPriceLookupKey(selectedTdTier.value) || null)
const tdProduct = computed(() => tdPricing.value?.product || null)
const tdPrice = computed(() => tdPricing.value?.price || null)

const qmsTdPlan = computed(() => props.plans?.getByPlan(TenantPlan.QMS_TD) || null)
const qmsTdPricing = computed(() => qmsTdPlan.value?.getByPriceLookupKey(selectedQmsTdTier.value) || null)
const qmsTdProduct = computed(() => qmsTdPricing.value?.product || null)
const qmsTdPrice = computed(() => qmsTdPricing.value?.price || null)

// Tier option builder for dropdown
interface TierOption { label: string; value: PriceLookupKeys }

function buildTierOptions(plan: PricingPlan | null, keys: PriceLookupKeys[]): TierOption[] {
    if (!plan) return []
    const currentIdx = props.currentPriceLookupKey ? keys.indexOf(props.currentPriceLookupKey as PriceLookupKeys) : -1
    return keys
        .filter((_, i) => currentIdx < 0 || i >= currentIdx)
        .map(key => {
            const pricing = plan.getByPriceLookupKey(key)
            if (!pricing?.price) return null
            const tier = key.slice(-1).toUpperCase()
            const credits = formatCredits(pricing.price.metadata?.credits)
            const isCurrent = props.currentPriceLookupKey === key
            const suffix = isCurrent ? ` - ${t('billing.tiers.current')}` : ''
            return { label: `${tier} (${credits} ${t('pricing.credits-per-month')})${suffix}`, value: key }
        })
        .filter((o): o is TierOption => o !== null)
}

function isCurrentTier(key: PriceLookupKeys): boolean {
    return props.currentPriceLookupKey === key
}

const qmsTierOptions = computed(() =>
    buildTierOptions(qmsPlan.value, [PriceLookupKeys.QMS_S, PriceLookupKeys.QMS_M, PriceLookupKeys.QMS_L])
)
const tdTierOptions = computed(() =>
    buildTierOptions(tdPlan.value, [PriceLookupKeys.TD_S, PriceLookupKeys.TD_M, PriceLookupKeys.TD_L])
)
const qmsTdTierOptions = computed(() =>
    buildTierOptions(qmsTdPlan.value, [PriceLookupKeys.QMS_TD_S, PriceLookupKeys.QMS_TD_M, PriceLookupKeys.QMS_TD_L])
)

function formatCredits(amount: number | null | undefined): string {
    return new Intl.NumberFormat(locale.value).format(amount ?? 0)
}

function getMetaLocalized(product: StripeProduct, key: 'title' | 'description' | 'marketing_features'): string {
    const meta = product.metadata || {}
    const lang = t('language').toLowerCase()
    const primaryKey = `${lang}_${key}` as const
    const fallbackKey = `en_${key}` as const
    const primary = (meta as Record<string, unknown>)[primaryKey]
    if (primary && typeof primary === 'string') return primary
    const fallback = (meta as Record<string, unknown>)[fallbackKey]
    if (fallback && typeof fallback === 'string') return fallback
    return ''
}

function getTitle(product: StripeProduct): string {
    return getMetaLocalized(product, 'title') || ''
}

function getDescription(product: StripeProduct): string {
    return getMetaLocalized(product, 'description') || ''
}

function getFeatures(product: StripeProduct): string {
    return getMetaLocalized(product, 'marketing_features')
}

// Price selection now comes from the store's pricing plans; no per-product selection needed

function formatEuro(amountCents: number | null | undefined): string {
    const amount = (amountCents ?? 0) / 100
    return new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(amount)
}

const currencySymbol = computed(() => t('pricing.currency.eur'))
const COUPON_CODE = 'MACEARLY'
</script>

<style scoped>
.pricing-card-wrapper {
    display: flex;
    flex-direction: column;
}

.pricing-card-wrapper :deep(.p-card) {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
}

.pricing-card-wrapper :deep(.p-card-body) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.pricing-card-wrapper :deep(.p-card-content) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}
</style>
